import {
    AutocompleteSource,
    AutocompleteState,
    BaseItem,
    createAutocomplete
} from "@algolia/autocomplete-core";
import { createRedirectUrlPlugin } from "@algolia/autocomplete-plugin-redirect-url";
import {
    hasRecentSearches,
    recentSearchesSource
} from "@omni/components/components/Search/SearchBar/components/ResultsPanel/components/Results/RecentSearches/recentSearches.source";
import { BffSearchProduct } from "@omni/types/api/search/bff/product/product.model";
import { debouncePromise } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import {
    BaseSyntheticEvent,
    KeyboardEvent,
    MouseEvent,
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { contentPagesSource } from "@/components/Search/SearchBar/components/ResultsPanel/components/Results/ContentPages/contentPages.source";
import { productsSource } from "@/components/Search/SearchBar/components/ResultsPanel/components/Results/Products/products.source";
import { querySuggestionsSource } from "@/components/Search/SearchBar/components/ResultsPanel/components/Results/QuerySuggestions/querySuggestions.source";
import {
    AutocompleteActions,
    AutocompleteRef
} from "@/components/Search/SearchBar/types/searchBar.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useSearchClient } from "@/utils/hooks/algolia/useSearchClient.hook";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";
import { useSearchIndexes } from "@/utils/hooks/useSearchIndexes.hook";
import { useSubmitAutocompleteSearchBox } from "@/utils/hooks/useSubmitAutocompleteSearchBox";
import { parseUrl } from "@/utils/search/routing/configurationFromUrl";

import { getRedirectUrlFromState } from "../helpers/redirect.helpers";

const debouncedPromise = debouncePromise(
    (items: Array<AutocompleteSource<any> | boolean | undefined>) =>
        Promise.resolve(items)
);

const createSetAutocompleteOpen =
    (
        setIsOpenStateUpdaters: Array<(value: boolean) => void>,
        isOpen: boolean,
        afterClose?: () => void
    ) =>
    (shouldOpen: boolean, force?: boolean) =>
    () => {
        if (isOpen === shouldOpen && !force) return;
        setIsOpenStateUpdaters.forEach((set) => set(shouldOpen));

        if (!shouldOpen) afterClose?.();
    };

type UseAutocompleteApiProps = {
    autocompleteRef: RefObject<AutocompleteRef>;
    initialProducts?: Array<BffSearchProduct>;
};

const redirectPlugin = createRedirectUrlPlugin();

const useAutocompleteApi = ({
    autocompleteRef,
    initialProducts
}: UseAutocompleteApiProps) => {
    const { t } = useGranite();

    const isClient = useIsClientSide();

    const searchClient = useSearchClient();
    const { productsIndex, contentPagesIndex, productsQuerySuggestionsIndex } =
        useSearchIndexes();

    const autocompleteStateRef = useRef<
        Partial<AutocompleteState<BaseItem>> | undefined
    >({});

    const [openState, setOpenState] = useState(false);
    const [autocompleteState, setAutocompleteState] = useState<{
        items: Record<string, BaseItem[]>;
        autocompleteState: AutocompleteState<BaseItem>;
        autocompleteActions: AutocompleteActions;
    }>();

    const getRedirectUrl = useCallback(() => {
        if (!isDefined(autocompleteStateRef.current)) return;

        return getRedirectUrlFromState(autocompleteStateRef.current);
    }, []);

    const { submit } = useSubmitAutocompleteSearchBox(getRedirectUrl);

    const autocomplete = useMemo(
        () =>
            createAutocomplete<
                BaseItem,
                BaseSyntheticEvent,
                MouseEvent,
                KeyboardEvent
            >({
                placeholder: t("searchBox.input.placeholder"),
                defaultActiveItemId: null,
                plugins: [redirectPlugin],
                openOnFocus: true,
                initialState: {
                    ...autocompleteStateRef.current,
                    isOpen: openState,
                    query:
                        autocompleteRef.current.query ??
                        autocompleteStateRef.current?.query
                },
                onStateChange({
                    state,
                    refresh,
                    setActiveItemId,
                    setQuery,
                    setIsOpen
                }) {
                    autocompleteStateRef.current = state;

                    const items = state.collections.reduce<
                        Record<string, BaseItem[]>
                    >(
                        (itemsAcc, collection) => ({
                            ...itemsAcc,
                            [collection.source.sourceId]: collection.items
                        }),
                        {}
                    );

                    setAutocompleteState({
                        items: items,
                        autocompleteState: state,
                        autocompleteActions: {
                            refresh: refresh,
                            setActiveItemId: setActiveItemId,
                            setQuery: (query: string) => {
                                if (autocompleteStateRef.current) {
                                    autocompleteStateRef.current.query = query;
                                }

                                autocompleteRef.current.query = query;
                                setQuery(query);
                            },
                            submit: (query: string) => {
                                autocompleteRef.current.query = query;
                                submit(query);
                                createSetAutocompleteOpen(
                                    [setIsOpen, setOpenState],
                                    openState
                                )(false, true)();
                            }
                        }
                    });
                },
                shouldPanelOpen(): boolean {
                    return true;
                },
                onSubmit({ state, setIsOpen }) {
                    submit(state.query);
                    createSetAutocompleteOpen(
                        [setIsOpen, setOpenState],
                        openState
                    )(false, true)();
                },
                getSources({ query, state }) {
                    const showRecentSearches = !query && hasRecentSearches();

                    const sources: Array<
                        AutocompleteSource<any> | boolean | undefined
                    > = [];
                    if (showRecentSearches) {
                        sources.push(recentSearchesSource);
                    }
                    if (productsQuerySuggestionsIndex) {
                        sources.push(
                            querySuggestionsSource(
                                productsQuerySuggestionsIndex,
                                searchClient
                            )
                        );
                    }
                    if (contentPagesIndex && query?.trim()) {
                        sources.push(
                            contentPagesSource(contentPagesIndex, searchClient)
                        );
                    }
                    if (productsIndex) {
                        sources.push(
                            productsSource(
                                productsIndex,
                                searchClient,
                                initialProducts
                            )
                        );
                    }

                    return !query || !state.isOpen
                        ? sources
                        : debouncedPromise(sources);
                }
            }),
        [
            productsIndex,
            contentPagesIndex,
            productsQuerySuggestionsIndex,
            t,
            searchClient,
            submit,
            autocompleteRef.current,
            initialProducts,
            openState
        ]
    );

    const setAutocompleteOpen = createSetAutocompleteOpen(
        [autocomplete.setIsOpen, setOpenState],
        openState,
        () => autocompleteRef.current.input?.focus()
    );

    useEffect(() => {
        if (!isClient) return;
        if (isDefined(autocompleteRef.current.query)) return;

        autocompleteRef.current.query = parseUrl().q ?? "";
    }, [isClient, autocompleteRef.current]);

    return {
        autocomplete: autocomplete,
        isOpen: openState,
        open: setAutocompleteOpen(true),
        close: setAutocompleteOpen(false),
        ...autocompleteState
    };
};

export { useAutocompleteApi };
